/**
 * Simple Login Page
 * 
 * Dedicated login page with content similar to AuthModal
 * Includes terms agreement and OAuth buttons
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useAuth } from '../stores/authStore';

const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized } = useAuth();
  const { login } = useAuthStore();

  // Get redirect URL from query params
  // If no redirect param, default to /my-account (better UX for first login)
  const redirectTo = searchParams.get('redirect') || '/my-account';

  // Redirect if already authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, redirectTo]);

  const handleGoogleAuth = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');
      setLoadingMessage('Connecting to authentication server...');
      
      // Save redirect URL to localStorage before OAuth redirect
      if (redirectTo && redirectTo !== '/my-account') {
        localStorage.setItem('auth_redirect', redirectTo);
      }
      
      // Use AuthStore to initiate Google OAuth
      await login('google');
      const response = { message: 'Redirecting to Google...' };
      
      // Show success message with green styling
      setSuccessMessage(response.message);
      setLoadingMessage('');
      
      // The user will be redirected to Google, then back to /auth/callback
      // No need to handle success here as it's handled in the callback
    } catch (error: any) {
      console.error('Google auth error:', error);
      setError(error.message || 'Google authentication failed');
      setLoadingMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookAuth = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');
      setLoadingMessage('Connecting to authentication server...');
      
      // Save redirect URL to localStorage before OAuth redirect
      if (redirectTo && redirectTo !== '/my-account') {
        localStorage.setItem('auth_redirect', redirectTo);
      }
      
      // Use AuthStore to initiate Facebook OAuth
      await login('facebook');
      const response = { message: 'Redirecting to Facebook...' };
      
      // Show success message with green styling
      setSuccessMessage(response.message);
      setLoadingMessage('');
      
      // The user will be redirected to Facebook, then back to /auth/callback
      // No need to handle success here as it's handled in the callback
    } catch (error: any) {
      console.error('Facebook auth error:', error);
      setError(error.message || 'Facebook authentication failed');
      setLoadingMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-8">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to Metapic.app
            </h1>
            <p className="text-gray-600">
              Sign in to continue to your account
            </p>
          </div>
          
          {/* Loading Message */}
          {isLoading && loadingMessage && (
            <div className="mb-4 p-3 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {loadingMessage}
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
              {successMessage}
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}

          {/* Google OAuth Button */}
          <button
            type="button"
            onClick={handleGoogleAuth}
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          {/* Facebook OAuth Button - DISABLED: Waiting for Facebook approval */}
          {/* <button
            type="button"
            onClick={handleFacebookAuth}
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-[#1877F2] hover:bg-[#166FE5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mt-3 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            {isLoading ? 'Signing in...' : 'Sign in with Facebook'}
          </button> */}
          
          {/* Separator */}
          <div className="mt-8 mb-6">
            <div className="border-t border-gray-200"></div>
          </div>
          
          {/* Terms Agreement */}
          <div className="text-center">
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              By signing in, you agree to our{' '}
              <a 
                href="/terms-of-service" 
                className="text-blue-600 hover:text-blue-700 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a 
                href="/privacy-policy" 
                className="text-blue-600 hover:text-blue-700 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>.
            </p>
            
            <p className="text-xs text-gray-400">
              We use your data to provide and improve our service.
            </p>
          </div>
          
          {/* Back to Home */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;